import {
  AutoAwesome,
  FolderShared,
  CalendarMonth,
  Receipt,
  Payment,
  Assignment,
  SwapHoriz,
  Person,
  Groups,
  Chat,
  Message,
  Group,
  Link as LinkIcon,
  Business
} from '@mui/icons-material';

export const featuresData = [
  // OPERATIONS
  {
    id: 'ai-agent',
    slug: 'ai-agent',
    title: 'AI Agent',
    pillars: ['operations'],
    icon: AutoAwesome,
    shortDescription: 'HIPAA-compliant Agentic AI that executes multi-step clinical tasks through natural language or secure email relay. Not just a chatbot, but a virtual colleague that follows a "No-PHI-in-Transport" standard for instructions while providing a "Human-in-the-loop" confirmation for every action.',
    tagline: 'Your Virtual Colleague',
    showcases: [
      {
        title: 'Multi-Step Task Execution',
        description: 'Our Agentic AI doesn\'t just answer questions—it executes multi-step tasks like creating clients, assigning task bundles, and generating clinical records. All critical clinical operations require intelligent human confirmation with preview and edit capabilities to ensure professional judgment remains central.',
        stat: '70% reduction in manual data entry',
        imagePosition: 'left',
        imageFile: 'assets/images/marketing/home/therapist-ai-chat-overlay.jpg'
      },
      {
        title: 'Natural Language Healthcare Operations',
        description: 'Create clients, update insurance records, assign tasks, and generate clinical documentation through simple conversation with our HIPAA-compliant agent. Deep healthcare domain expertise understands clinical terminology and workflows.',
        stat: '80% reduction in onboarding time',
        imagePosition: 'right',
        imageFile: 'assets/images/marketing/features/ai/ai-natural-language-diagram.jpg'
      },
      {
        title: 'Dual-Agent Architecture',
        description: 'Choose between a powerful Standard Agent for intelligent, fast responses that use less credits, or an even more powerful Advanced Agent for tasks that require complex reasoning. Both maintain complete audit trails with transparent credit usage and cost tracking.',
        stat: 'Transparent cost control',
        imagePosition: 'left',
        imageFile: 'assets/images/marketing/features/ai/ai-dual-model-diagram.jpg'
      },
      {
        title: 'Intelligent Document Processing',
        description: 'Upload intake forms, insurance cards, or clinical documents and let AI extract structured data to auto-create client records. Supports PDFs, images, and Word documents with a high degree of accuracy.',
        stat: 'Eliminate manual data entry',
        imagePosition: 'right',
        imageFile: 'assets/images/marketing/features/ai/ai-document-extraction-diagram.jpg'
      },
      {
        title: 'AI Agent via Email - Work From Anywhere',
        description: 'Interact with your AI assistant from anywhere by sending instructions or forwarding consented client communications to agent@relay.halespring.com. Our system intelligently parses forwarded threads to create records, while enforcing a strict policy against including PHI in the subject or body of general email instructions. Every action is staged in your secure Email Inbox for review and explicit confirmation before being finalized.',
        stat: 'AI assistance from anywhere',
        imagePosition: 'left',
        imageFile: 'assets/images/marketing/features/ai/ai-agent-email-forwarding.jpg'
      }
    ],
    useCases: [
      {
        title: 'Automated Client Onboarding',
        description: 'Say "Create new client John Smith with XYZ characteristics" and AI handles profile creation, task assignment, and email notifications with your confirmation.'
      },
      {
        title: 'Document Intelligence',
        description: 'Upload intake forms and let AI extract structured data to auto-create client records, insurance information, and clinical assessments.'
      },
      {
        title: 'Clinical Documentation',
        description: 'Generate session notes, treatment plans, and assessment records from natural language descriptions with proper clinical formatting.'
      },
      {
        title: 'Smart Search & Analysis',
        description: 'Find client information with complex criteria like "all clients with Blue Cross insurance created this month" and get instant structured results.'
      },
      {
        title: 'Remote AI Assistance via Email',
        description: 'Forward emails from clients or referral sources containing intake forms and clinical records only when the recipient has opted into email communication. Our AI intelligently processes this content into structured clinical records, which are then queued for your manual review and approval.'
      }
    ],
    relatedFeatures: ['client-management', 'tasks', 'scheduling']
  },
  {
    id: 'client-management',
    slug: 'client-management',
    title: 'Client Management',
    pillars: ['operations'],
    icon: FolderShared,
    shortDescription: 'Complete clinical documentation with pre-built mental health templates plus unlimited custom forms using 11 field types, calculated fields, integrated billing & payments, and multi-provider collaboration.',
    tagline: 'Comprehensive Clinical Records',
    showcases: [
      {
        title: 'Advanced Template System',
        description: 'Pre-built mental health templates (GAD-7 with auto-scoring, PHQ-9, Crisis Safety Plans) plus unlimited custom templates using 11 field types, calculated fields, and automatic scoring formulas. Build any assessment tool your practice needs.',
        stat: 'Fully customizable',
        imagePosition: 'left',
        imageFile: 'assets/images/marketing/features/clients/custom-template.jpg'
      },
      {
        title: 'Multi-Provider Collaboration',
        description: 'Invite external providers to collaborate with reciprocal client records, unified messaging channels, and secure authorization workflows. Perfect for care teams with therapists, psychiatrists, and case managers.',
        stat: 'Seamless care coordination',
        imagePosition: 'right',
        imageFile: 'assets/images/marketing/features/clients/client-provider-list.jpg'
      },
      {
        title: 'Complete Audit Trail & Version Control',
        description: 'Every field change tracked with full audit trail and one-click revert capability. Know exactly who changed what and when, with complete HIPAA compliance and professional PDF generation.',
        stat: 'Complete version history',
        imagePosition: 'left',
        imageFile: 'assets/images/marketing/features/clients/record-audit-trail.jpg'
      },
      {
        title: 'Integrated Payment Processing',
        description: 'Built-in Stripe integration with client payment method storage, auto-pay enrollment, and automatic billing. Clients save payment methods once, then automatic charges process when superbills are finalized.',
        stat: '50% faster payment collection',
        imagePosition: 'right',
        imageFile: 'assets/images/marketing/features/billing/client-billing-records.jpg'
      }
    ],
    useCases: [
      {
        title: 'Custom Assessment Tools',
        description: 'Build unlimited custom forms with calculated fields and automatic scoring for your specific practice needs. From DBT diary cards to specialized outcome measures.'
      },
      {
        title: 'Automated Client Onboarding',
        description: 'Email verification workflows, automated task assignment, and status progression from pending to established with configurable onboarding flows.'
      },
      {
        title: 'Family-Centered Care',
        description: 'Bidirectional relationship mapping enables parent/child workflows and family member task assignment with complex family structure support.'
      },
      {
        title: 'Integrated Billing Workflow',
        description: 'Client demographics auto-populate superbills, payment methods enable automatic billing, and diagnosis codes suggest from client records.'
      }
    ],
    relatedFeatures: ['ai-agent', 'tasks', 'provider-collaboration']
  },
  {
    id: 'scheduling',
    slug: 'scheduling',
    title: 'Scheduling',
    pillars: ['operations'],
    icon: CalendarMonth,
    shortDescription: 'Scheduling with a dual-track system: Client Sessions for therapy appointments and Meetings for colleague collaboration. Features 24/7 public booking, sophisticated recurring sessions, and bidirectional sync with Google, Outlook, Apple, and Exchange.',
    tagline: 'Your Digital Front Door',
    showcases: [
      {
        title: 'Dual-Track Scheduling System',
        description: 'Separate but integrated workflows for Client Sessions (with billing integration) and Colleague Meetings. Real-time availability prevents double-booking across all calendar platforms through integrated calendar sync.',
        stat: 'Zero double-bookings',
        imagePosition: 'left',
        imageFile: 'assets/images/marketing/features/scheduling/provider-calendar.jpg'
      },
      {
        title: '24/7 Public Booking',
        description: 'Enable new clients to book appointments online without creating accounts. Rate-limited booking with email verification, automatic client creation, and real-time availability checking reduce phone calls by 75%.',
        stat: '75% reduction in phone calls',
        imagePosition: 'right',
        imageFile: 'assets/images/marketing/features/scheduling/public-booking.jpg'
      },
      {
        title: 'Intelligent Recurring Sessions',
        description: 'Rolling window auto-generates sessions 6 months out with sophisticated update scopes: modify "This Occurrence," "This and Future," or "All" with smart conflict resolution and automatic calendar sync.',
        stat: 'Automated session management',
        imagePosition: 'left',
        imageFile: 'assets/images/marketing/features/scheduling/client-sessions.jpg'
      },
      {
        title: 'Integrated Billing Workflow',
        description: 'Completed sessions automatically create billing records with CPT codes, place of service codes (02 for telehealth), and modifiers. One-click superbill generation ready for insurance submission.',
        stat: '50% faster billing workflow',
        imagePosition: 'right',
        imageFile: 'assets/images/marketing/features/billing/client-billing-records.jpg'
      }
    ],
    useCases: [
      {
        title: 'Client Self-Booking',
        description: 'New clients book appointments online 24/7 with instant confirmation, email verification, and automatic client record creation - no phone calls required.'
      },
      {
        title: 'Enterprise Calendar Sync',
        description: 'Bidirectional sync with Google, Outlook, Apple, and Exchange calendars with real-time webhook notifications and conflict resolution.'
      },
      {
        title: 'Telehealth Practice',
        description: 'Automatic place of service codes (02) and telehealth modifiers (95) ensure compliant billing with integrated session-to-billing record creation.'
      },
      {
        title: 'Recurring Session Management',
        description: 'Set up weekly therapy sessions in seconds with flexible modification options - change single occurrences or entire series with smart update scopes.'
      }
    ],
    relatedFeatures: ['billing', 'tasks', 'ai-agent']
  },
  {
    id: 'billing',
    slug: 'billing',
    title: 'Billing',
    pillars: ['operations'],
    icon: Receipt,
    shortDescription: 'One-click billing powered by Snapshot technology that captures an immutable record of client, provider, and insurance data at the moment of creation. Generate CMS-1500 compatible superbills that remain audit-proof even if underlying records are updated later.',
    tagline: 'Audit-Proof Revenue Cycle',
    showcases: [
      {
        title: 'One-Click Billing from Sessions',
        description: 'Create billing records directly from completed sessions with zero manual data entry. All fields auto-populate from session data including CPT codes, diagnosis suggestions, place of service, and modifiers.',
        stat: 'Zero manual data entry',
        imagePosition: 'left',
        imageFile: 'assets/images/marketing/features/billing/create-billing-record-from-session.jpg'
      },
      {
        title: 'Snapshot Technology for Insurance Reviews',
        description: 'Halespring captures a frozen state of client, provider, and insurance data at the exact time of superbill creation. This gives insurance reviews a stable record, ensuring your billing records remain a true "Point-of-Service" reflection regardless of subsequent record changes.',
        stat: 'Audit-proof records',
        imagePosition: 'right',
        imageFile: 'assets/images/marketing/features/clients/client-audit-history.jpg'
      },
      {
        title: 'Professional CMS-1500 Superbills',
        description: 'Generate professional, insurance-ready superbills in under 1 second. Batch multiple billing records, auto-populate provider and client information, and deliver securely via encrypted task system.',
        stat: '< 1 second generation',
        imagePosition: 'left',
        imageFile: 'assets/images/marketing/features/billing/superbill-pdf.jpg'
      },
      {
        title: 'Integrated Payment Processing',
        description: 'Automatic payment capture for clients with saved cards, or professional Stripe invoice generation. Real-time payment status tracking with automatic billing record updates when payments are received.',
        stat: 'Automatic payment processing',
        imagePosition: 'right',
        imageFile: 'assets/images/marketing/features/billing/client-payment-method.jpg'
      }
    ],
    useCases: [
      {
        title: 'One-Click Session Billing',
        description: 'Create billing records directly from completed sessions with zero manual data entry - all fields auto-populate from session data including CPT codes and diagnosis suggestions.'
      },
      {
        title: 'Smart Diagnosis Coding',
        description: 'Intelligent auto-suggest based on client\'s active diagnoses reduces coding errors and ensures consistent documentation across billing records.'
      },
      {
        title: 'Batch Superbill Generation',
        description: 'Generate and send CMS-1500 compatible superbills for multiple sessions at once with secure task-based delivery to clients.'
      },
      {
        title: 'Automated Payment Collection',
        description: 'Finalize superbills and automatically charge saved payment methods, or send professional Stripe invoices with real-time payment tracking.'
      }
    ],
    relatedFeatures: ['payments', 'scheduling', 'client-management']
  },
  {
    id: 'payments',
    slug: 'payments',
    title: 'Payments',
    pillars: ['operations'],
    icon: Payment,
    shortDescription: 'Privacy-shielded payment collection integrated with Stripe. Process automatic charges or professional invoices that maintain a clear boundary between financial transactions and sensitive PHI.',
    tagline: 'Seamless Payment Processing',
    showcases: [
      {
        title: 'Stripe Integration & Direct Deposits',
        description: 'Connect your Stripe account to receive payments directly to your bank account. Full control over payout schedule and settings with professional dashboard access and transparent pricing.',
        stat: 'Direct bank deposits',
        imagePosition: 'left',
        imageFile: 'assets/images/marketing/features/payments/stripe-setup.jpg'
      },
      {
        title: 'Automatic Payment with Smart Fallback',
        description: 'System automatically charges saved payment methods when superbills are finalized, or gracefully falls back to professional Stripe invoices. Automatic reminders and payment tracking included.',
        stat: 'Automated payment processing',
        imagePosition: 'right',
        imageFile: 'assets/images/marketing/features/payments/superbill-payment.jpg'
      },
      {
        title: 'Saved Payment Methods & Auto-Pay',
        description: 'Clients securely save payment methods for future use. Optional auto-pay enrollment enables automatic billing for recurring clients with instant payment capture and PCI-compliant security.',
        stat: 'Secure auto-pay',
        imagePosition: 'left',
        imageFile: 'assets/images/marketing/features/billing/client-payment-method.jpg'
      },
      {
        title: 'Privacy-Shielded Invoicing',
        description: 'Our professional invoices facilitate modern payment methods without exposing PHI or diagnostic details to financial processors. While clients receive a clear, itemized bill, the clinical specifics of their care remain secured within the HIPAA-protected Halespring environment.',
        stat: 'Privacy-shielded invoicing',
        imagePosition: 'right',
        imageFile: 'assets/images/marketing/features/payments/stripe-invoice-pdf.jpg'
      }
    ],
    useCases: [
      {
        title: 'Automatic Payment Capture',
        description: 'Clients enroll in auto-pay for automatic charging when superbills are finalized - no manual invoicing or follow-up required.'
      },
      {
        title: 'Secure Payment Methods',
        description: 'PCI-compliant card storage and management through Stripe with embedded or hosted collection flows for maximum security.'
      },
      {
        title: 'Real-Time Payment Tracking',
        description: 'Monitor payment status (Pending, Processing, Paid, Failed) directly in superbill table with automatic status updates via webhooks.'
      }
    ],
    relatedFeatures: ['billing', 'client-management', 'scheduling']
  },
  {
    id: 'tasks',
    slug: 'tasks',
    title: 'Tasks',
    pillars: ['operations', 'care'],
    icon: Assignment,
    shortDescription: 'Comprehensive workflow automation featuring identity-verified task completion. Deploy Form Entry, File Exchange, and HIPAA Release workflows with optional phone verification (OTP) to ensure non-repudiation for sensitive clinical documents.',
    tagline: 'Workflow Automation Platform',
    showcases: [
      {
        title: 'Intelligent Task Types',
        description: 'Form Entry tasks support multiple templates in sequence with progress tracking. File Exchange enables bidirectional document sharing. HIPAA Release workflows include electronic signatures with automatic PDF generation.',
        stat: '80% faster onboarding',
        imagePosition: 'left',
        imageFile: 'assets/images/marketing/features/tasks/client-task-list.jpg'
      },
      {
        title: 'Flexible Assignment System',
        description: 'Assign tasks to clients, family members, or external providers. Intelligent targeting based on relationships with automatic user resolution and email fallback for external users.',
        stat: 'Multi-target assignment',
        imagePosition: 'right',
        imageFile: 'assets/images/marketing/features/tasks/client-intake-task-email.jpg'
      },
      {
        title: 'Identity-Verified Security & Compliance',
        description: 'Protect your practice with optional phone verification (OTP) for sensitive tasks like HIPAA Releases and clinical assessments. This added security layer ensures that high-risk documents are completed by the verified individual, providing a superior level of certainty for medical-legal documentation.',
        stat: 'Identity-verified workflows',
        imagePosition: 'left',
        imageFile: 'assets/images/marketing/features/tasks/client-task-mfa.jpg'
      },
      {
        title: 'Seamless Integration & Automation',
        description: 'Tasks automatically create client records from completed forms. Multi-template tasks track progress across forms. Public access via secure codes requires no platform accounts for external users.',
        stat: 'Zero manual data entry',
        imagePosition: 'right',
        imageFile: 'assets/images/marketing/features/tasks/client-intake-task-public-form.jpg'
      }
    ],
    useCases: [
      {
        title: 'Automated Client Onboarding',
        description: 'Assign multiple intake forms in sequence with automatic progress tracking and record creation - reduce onboarding time by 80%.'
      },
      {
        title: 'Family-Centered Care',
        description: 'Assign tasks to parents or caregivers for child clients with relationship-aware targeting and automatic record linking.'
      },
      {
        title: 'Provider Collaboration',
        description: 'Securely exchange documents with external providers using OTP verification and bidirectional file sharing.'
      },
      {
        title: 'Document Collection',
        description: 'Request insurance cards, IDs, and medical records with automatic organization and 70% faster collection times.'
      }
    ],
    relatedFeatures: ['client-management', 'ai-agent', 'direct-messages']
  },

  // NETWORK
  {
    id: 'referrals',
    slug: 'referrals',
    title: 'Referrals',
    pillars: ['network', 'care'],
    icon: SwapHoriz,
    shortDescription: 'Comprehensive referral exchange designed to solve the "Access Gap" using 20+ search criteria, including client preferences for provider gender, age, race, and language. Features four specialized referral types, real-time notifications, and dedicated DM channels to reduce placement times from months to days.',
    tagline: 'Intelligent Referral Marketplace',
    showcases: [
      {
        title: 'Advanced Criteria Search Engine',
        description: 'Search by specializations, insurance, location, AND client preferences for provider gender, age, race, and language. Distance-based filtering with customizable radius from 5 to 250 miles ensures precise matching. This relational care architecture ensures patients find the right clinical and cultural fit, moving past the average 4 to 6 month wait time for new appointments.',
        stat: 'Find the right cases',
        imagePosition: 'left',
        imageFile: 'assets/images/marketing/features/referrals/referral-search-filters.jpg'
      },
      {
        title: 'Six Referral Types & Real-Time Matching',
        description: 'Manage New Client referrals, Transitioning Client handoffs, Collaboration requests, and Group/Program placements. Real-time notifications alert matching providers instantly with customizable notification filters, enabling a responsive care network that addresses the unmet needs of the 137 million Americans in shortage areas.',
        stat: 'Faster placement',
        imagePosition: 'right',
        imageFile: 'assets/images/marketing/features/referrals/referral-detail.jpg'
      },
      {
        title: 'Dedicated Communication Channels',
        description: 'Each referral interest automatically creates a secure, HIPAA-compliant DM channel between providers. Coordinate care transitions, share additional clinical information, and maintain a synchronous "Human-in-the-loop" coordination process throughout the patient handoff.',
        stat: 'Seamless coordination',
        imagePosition: 'left',
        imageFile: 'assets/images/marketing/features/referrals/referral-chat.jpg'
      },
      {
        title: 'Privacy Controls & Cultural Matching',
        description: 'Utilize unlisted referrals for direct sharing, anonymous requestors for privacy, and comprehensive cultural matching including provider language, gender, age, and race preferences. This ensures optimal client-provider fit and strengthens the therapeutic alliance from the first contact.',
        stat: 'Better patient/provider fit',
        imagePosition: 'right',
        imageFile: 'assets/images/marketing/features/referrals/referral-share.png'
      }
    ],
    useCases: [
      {
        title: 'Culturally Matched Referrals',
        description: 'Find providers matching specific client preferences for gender, age, race, and language - ensuring optimal therapeutic relationships and improved outcomes.'
      },
      {
        title: 'Urgent Placement with Real-Time Alerts',
        description: 'Critical referrals trigger instant notifications to matching providers, reducing placement time from weeks to days for urgent cases.'
      },
      {
        title: 'Higher Level of Care Transitions',
        description: 'Seamlessly coordinate transitions to IOP, PHP, or residential programs with specialized referral types and dedicated communication channels.'
      },
      {
        title: 'Practice Capacity Management',
        description: 'Set up notification filters matching your expertise and availability to receive qualified referrals that fit your practice perfectly.'
      }
    ],
    relatedFeatures: ['profiles', 'direct-messages', 'organizations']
  },
  {
    id: 'profiles',
    slug: 'profiles',
    title: 'Profiles',
    pillars: ['network'],
    icon: Person,
    shortDescription: 'Comprehensive professional profiles acting as your practice\'s "Digital Front Door". Featuring 20+ healthcare-specific fields, SEO-optimized public presence, direct booking integration, and intelligent discovery tools for clients and colleagues.',
    tagline: 'Your Professional Digital Presence',
    showcases: [
      {
        title: 'Rich Professional Profiles',
        description: 'Create comprehensive profiles with 20+ healthcare-specific fields including credentials, specializations, insurance accepted, practice states, and detailed service information. Utilize a full WYSIWYG bio editor to share your treatment philosophy and custom fee schedules.',
        stat: '20+ professional fields',
        imagePosition: 'left',
        imageFile: 'assets/images/marketing/features/profiles/profile-settings.jpg'
      },
      {
        title: 'SEO-Optimized Public Presence',
        description: 'Modern public profile pages built on a secure AWS-cloud infrastructure help prospective clients discover you through search engines. Toggle visibility controls, upload professional imagery, and utilize mobile-optimized layouts to establish your brand within the community.',
        stat: 'Get discovered by clients',
        imagePosition: 'right',
        imageFile: 'assets/images/marketing/features/profiles/public-profile.jpg'
      },
      {
        title: 'Direct Booking & Scheduling Integration',
        description: 'Clients can book sessions and colleagues can schedule meetings directly from your profile. Integrated availability display, real-time booking, and automatic calendar sync streamline appointment setting and reduce administrative drag.',
        stat: 'One-click booking',
        imagePosition: 'left',
        imageFile: 'assets/images/marketing/features/profiles/public-booking.jpg'
      },
      {
        title: 'Intelligent Provider Discovery',
        description: 'Advanced search and filtering by specializations, location, insurance, and credentials. Suggested provider recommendations, favorites system, and custom tagging help clients and colleagues find the clinical match they need.',
        stat: 'Smart provider matching',
        imagePosition: 'right',
        imageFile: 'assets/images/marketing/features/profiles/provider-search-filters.jpg'
      }
    ],
    useCases: [
      {
        title: 'Client Acquisition',
        description: 'SEO-optimized public profiles help potential clients discover your services through Google searches and direct referrals.'
      },
      {
        title: 'Professional Networking',
        description: 'Detailed professional information helps colleagues understand your expertise, credentials, and availability for referrals and collaboration.'
      },
      {
        title: 'Streamlined Booking',
        description: 'Integrated booking and scheduling allows clients and colleagues to book appointments directly from your profile without phone calls.'
      },
      {
        title: 'Practice Management',
        description: 'Comprehensive profile information integrates with client management, billing, and scheduling systems for seamless workflows.'
      }
    ],
    relatedFeatures: ['referrals', 'social-community', 'organizations']
  },
  {
    id: 'social-community',
    slug: 'social-community',
    title: 'Social & Community',
    pillars: ['network'],
    icon: Groups,
    shortDescription: 'Professional clinical networking featuring discussion groups, multi-tier connection systems, timeline insights, and long-form article publishing to share professional knowledge and establish thought leadership.',
    tagline: 'Professional Social Networking',
    showcases: [
      {
        title: 'Build Professional Connections',
        description: 'Three-tier connection system with Connections (mutual relationships with DM access), Following (see public posts in feed), and Followers (your audience). Organize contacts with favorites, custom tags, and private clinical notes.',
        stat: 'Multi-tier networking',
        imagePosition: 'right',
        imageFile: 'assets/images/marketing/features/social/connections.jpg'
      },
      {
        title: 'Professional Discussion Groups',
        description: 'Join Special Interest Groups (SIGs) for peer consultation, specialty-focused discussion, and clinical knowledge sharing. Groups support social media-style posts, likes, comments, and threaded discussions for secure, asynchronous collaboration.',
        stat: 'Connect with specialty peers',
        imagePosition: 'left',
        imageFile: 'assets/images/marketing/features/social/group.jpg'
      },
      {
        title: 'Rich Content Publishing & Feed',
        description: 'Share insights via timeline posts with images, videos, and link previews. Publish long-form articles with a WYSIWYG editor to share clinical guides and case studies. Your personalized feed highlights content from followed users and featured professional insights.',
        stat: 'Establish thought leadership',
        imagePosition: 'right',
        imageFile: 'assets/images/marketing/features/social/home-screen.jpg'
      },
      {
        title: 'Professional Recommendations',
        description: 'Write and receive professional recommendations that appear on profiles. Build credibility through peer endorsements and showcase your expertise to potential clients and referral partners.',
        stat: 'Build professional credibility',
        imagePosition: 'left',
        imageFile: 'assets/images/marketing/features/social/create-recommendation.jpg'
      }
    ],
    useCases: [
      {
        title: 'Thought Leadership',
        description: 'Publish articles and posts to establish expertise in your specialization and build professional reputation within the mental health community.'
      },
      {
        title: 'Professional Networking',
        description: 'Connect with peers, follow thought leaders, and build meaningful relationships through mutual connections and direct messaging.'
      },
      {
        title: 'Knowledge Sharing',
        description: 'Share insights, research, and best practices through posts and articles. Engage with colleagues through comments and discussions.'
      },
      {
        title: 'Peer Recognition',
        description: 'Give and receive professional recommendations to build credibility and showcase expertise to potential clients and referral partners.'
      }
    ],
    relatedFeatures: ['profiles', 'direct-messages', 'teams']
  },
  {
    id: 'direct-messages',
    slug: 'direct-messages',
    title: 'Direct Messages',
    pillars: ['network', 'care'],
    icon: Message,
    shortDescription: 'Private, HIPAA-compliant real-time messaging for secure coordination with colleagues, clients, and families. Features context-aware dashboards, video calls, and file sharing to ensure clinical communications are centralized and audit-ready.',
    tagline: 'Secure Private Communication',
    showcases: [
      {
        title: 'Unified Message Dashboard',
        description: 'A centralized context-aware dashboard showing activity across all communication channels—Teams, Groups, Direct Messages, Client channels, Referrals, and Organizations. Filter by type and monitor unread indicators to ensure no urgent clinical update is missed.',
        stat: 'All messages in one place',
        imagePosition: 'left',
        imageFile: 'assets/images/marketing/features/messaging/message-dashboard.jpg'
      },
      {
        title: 'Powerful Message Search',
        description: 'Search across all your Direct Messages and Teams with keyword filtering, date ranges, and channel-specific searches. Find any specific clinical conversation or shared resource instantly.',
        stat: 'Search all conversations',
        imagePosition: 'right',
        imageFile: 'assets/images/marketing/features/messaging/message-search.jpg'
      },
      {
        title: 'Automatic Referral Coordination',
        description: 'Expressing interest in a referral automatically creates a secure DM channel with the referring provider. This streamlines handoff discussions and ensures that the transition of care remains coordinated and patient-centered.',
        stat: 'Seamless referral coordination',
        imagePosition: 'left',
        imageFile: 'assets/images/marketing/features/referrals/referral-chat.jpg'
      },
      {
        title: 'Video Calls & Rich Communication',
        description: 'Start secure video calls instantly via Chime integration. Share clinical files up to 50MB, utilize read receipts, and flag important messages to trigger immediate email notifications for urgent situations.',
        stat: 'Complete communication suite',
        imagePosition: 'right',
        imageFile: 'assets/images/marketing/features/messaging/video-call-with-sharing.jpg'
      }
    ],
    useCases: [
      {
        title: 'Provider Consultation',
        description: 'Quick consultations with colleagues about cases, treatment approaches, or professional questions with instant delivery and read confirmation.'
      },
      {
        title: 'Referral Coordination',
        description: 'Automatic DM creation when expressing referral interest streamlines provider-to-provider communication and seamless client handoffs.'
      },
      {
        title: 'Client Communication',
        description: 'Secure messaging with clients between sessions for sharing resources and coordinating care with HIPAA-compliant encryption.'
      },
      {
        title: 'Crisis Communication',
        description: 'Important message flags ensure critical communications trigger immediate email notifications for urgent situations and emergencies.'
      }
    ],
    relatedFeatures: ['referrals', 'teams', 'social-community']
  },
  {
    id: 'teams',
    slug: 'teams',
    title: 'Teams',
    pillars: ['network', 'care'],
    icon: Group,
    shortDescription: 'Multidisciplinary care coordination workspaces featuring private "Patient Rooms" for active treatment teams. Includes dedicated clinical file storage, video calling, and focused Topics for synchronized care planning.',
    tagline: 'Real-Time Care Coordination',
    showcases: [
      {
        title: 'Private Care Coordination Workspaces',
        description: 'Create dedicated, private "Patient Rooms" for multidisciplinary treatment teams involving therapists, psychiatrists, and case managers. Real-time messaging with instant delivery and organized Topics keep complex care coordination synchronized and secure.',
        stat: 'Private by default',
        imagePosition: 'left',
        imageFile: 'assets/images/marketing/features/teams/team-chat.jpg'
      },
      {
        title: 'Persistent File Storage & Organization',
        description: 'Each team features a dedicated folder structure for treatment plans, assessments, and shared clinical resources. Hierarchical organization ensures all team members have access to critical documents, up to 50MB per file.',
        stat: 'Organized team resources',
        imagePosition: 'right',
        imageFile: 'assets/images/marketing/features/teams/team-file-folders.jpg'
      },
      {
        title: 'Client Integration & Topics',
        description: 'Link teams to specific clients and create dedicated discussion Topics for focused coordination. Invite family members or parents as guest members with limited permissions to improve transparency and outcomes.',
        stat: 'Client-centered coordination',
        imagePosition: 'left',
        imageFile: 'assets/images/marketing/features/teams/team-client-channel.jpg'
      },
      {
        title: 'Video Calling & Real-Time Features',
        description: 'Start secure video calls instantly via Chime for case consultations and multidisciplinary meetings. Real-time messaging and search ensure immediate response to evolving clinical situations.',
        stat: 'Instant team meetings',
        imagePosition: 'right',
        imageFile: 'assets/images/marketing/features/messaging/video-call-with-sharing.jpg'
      }
    ],
    useCases: [
      {
        title: 'Multidisciplinary Care Teams',
        description: 'Coordinate between therapists, psychiatrists, case managers, and primary care providers with private workspaces and persistent file storage.'
      },
      {
        title: 'Crisis Response Coordination',
        description: 'Real-time communication with instant delivery enables immediate team response to client crises with all providers notified instantly.'
      },
      {
        title: 'Treatment Planning & Documentation',
        description: 'Collaborative treatment planning with shared documents, organized file storage, and focused Topics for different aspects of care.'
      },
      {
        title: 'Client Engagement & Transparency',
        description: 'Include clients as guest members with limited permissions to improve transparency, engagement, and care coordination outcomes.'
      }
    ],
    relatedFeatures: ['direct-messages', 'client-management', 'provider-collaboration']
  },

  // CARE
  {
    id: 'provider-collaboration',
    slug: 'provider-collaboration',
    title: 'Provider Collaboration',
    pillars: ['care'],
    icon: LinkIcon,
    shortDescription: 'Seamless external integration enabling providers to share a "synchronized vision" through reciprocal client records, secure authorization workflows, and unified communication.',
    tagline: 'Seamless External Integration',
    showcases: [
      {
        title: 'Reciprocal Client Records',
        description: 'Invite external providers to collaborate with synchronized access to client documentation. Both providers view the same clinical context, ensuring care is coordinated without the risk of duplicate data entry or siloing.',
        stat: 'Unified client records',
        imagePosition: 'left',
        imageFile: 'assets/images/marketing/features/clients/client-provider-list.jpg'
      },
      {
        title: 'Secure Authorization & ROI Workflows',
        description: 'Built-in HIPAA release (ROI) workflows with electronic signatures ensure legally compliant information sharing. Automatic authorization tracking and renewal reminders maintain compliance across the care continuum.',
        stat: 'HIPAA-compliant sharing',
        imagePosition: 'right',
        imageFile: 'assets/images/marketing/features/collaboration/client-provider-roi.png'
      },
      {
        title: 'Unified Communication Channels',
        description: 'Shared messaging channels and care teams bridge the gap between therapists, prescribing doctors, and families. Coordinate treatment plans and share real-time updates through secure, encrypted messaging.',
        stat: 'Real-time coordination',
        imagePosition: 'left',
        imageFile: 'assets/images/marketing/features/teams/team-client-channel.jpg'
      },
      {
        title: 'Task-Based Information Exchange',
        description: 'Request specific treatment summaries or assessments from external partners through secure task assignments. Specialized handling for sensitive data (such as 42 CFR Part 2) ensures practice-wide integrity.',
        stat: 'Streamlined information exchange',
        imagePosition: 'right',
        imageFile: 'assets/images/marketing/features/tasks/client-file-task-public-form.jpg'
      }
    ],
    useCases: [
      {
        title: 'Multidisciplinary Care Teams',
        description: 'Coordinate between internal and external providers with shared client records and unified communication channels.'
      },
      {
        title: 'Specialist Consultations',
        description: 'Seamlessly collaborate with specialists while maintaining unified client records and communication history.'
      },
      {
        title: 'Care Transitions',
        description: 'Smooth handoffs between providers with complete information sharing and collaborative transition planning.'
      },
      {
        title: 'Treatment Coordination',
        description: 'Real-time coordination of complex treatment plans involving multiple external providers and specialties.'
      }
    ],
    relatedFeatures: ['teams', 'tasks', 'client-management']
  },
  {
    id: 'organizations',
    slug: 'organizations',
    title: 'Organizations',
    pillars: ['network', 'care'],
    icon: Business,
    shortDescription: 'Enterprise practice management for group practices and treatment centers. Unify multidisciplinary care with centralized referral intake, program management, and SEO-optimized public profiles to expand workforce capacity.',
    tagline: 'Scale Your Practice',
    showcases: [
      {
        title: 'Practice Management Platform',
        description: 'Manage your entire practice hub—team communication, client records, and service programs—across multiple clinical locations. Built-in collaboration tools keep multidisciplinary teams connected within a unified HIPAA-compliant system.',
        stat: 'All-in-one practice solution',
        imagePosition: 'left',
        imageFile: 'assets/images/marketing/features/organizations/org-client-list.jpg'
      },
      {
        title: 'Centralized Referral Intake System',
        description: 'Manage clinical volume through a dedicated organization-level referral channel. Multiple staff can review and coordinate admissions, with automatic DM creation to streamline the handoff from referring providers.',
        stat: 'Centralized referral management',
        imagePosition: 'right',
        imageFile: 'assets/images/marketing/features/organizations/org-referrals.jpg'
      },
      {
        title: 'Clinical Program & Capacity Management',
        description: 'Manage specialized programs (IOP/PHP) with location-specific relationships. Client teams and authorized user workflows expand your practice\'s care capacity by reducing administrative roadblocks.',
        stat: 'Unified practice management',
        imagePosition: 'left',
        imageFile: 'assets/images/marketing/features/organizations/org-manage-programs.jpg'
      },
      {
        title: 'SEO-Optimized Public Profiles',
        description: 'High-visibility public profiles with custom URLs help patients discover your programs and facilities through search engines. Showcase your entire clinical team and specialized service areas with professional branding.',
        stat: 'Patient acquisition',
        imagePosition: 'right',
        imageFile: 'assets/images/marketing/features/organizations/org-program-profile.jpg'
      }
    ],
    useCases: [
      {
        title: 'Multi-Location Group Practice',
        description: 'Manage multiple providers, locations, and programs with centralized client records, referral intake, and team collaboration across all sites.'
      },
      {
        title: 'Treatment Center Operations',
        description: 'Showcase IOP, PHP, and residential programs with detailed service information, location relationships, and centralized admissions process.'
      },
      {
        title: 'Centralized Referral Management',
        description: 'Receive and manage referrals from providers across the platform with dedicated referral channel and team-based review process.'
      },
      {
        title: 'Professional Credibility & Discovery',
        description: 'SEO-optimized public profiles help patients find your services and providers discover your programs through search engines and platform discovery.'
      }
    ],
    relatedFeatures: ['teams', 'client-management', 'referrals']
  }
];

export const getPillarFeatures = (pillar) => {
  return featuresData.filter(f => 
    Array.isArray(f.pillars) ? f.pillars.includes(pillar) : f.pillar === pillar
  );
};

export const getFeatureBySlug = (slug) => {
  return featuresData.find(f => f.slug === slug);
};

export const getRelatedFeatures = (slugs) => {
  return slugs.map(slug => featuresData.find(f => f.slug === slug)).filter(Boolean);
};
